import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { Popover, Modal, useMediaQuery, useTheme } from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import CloseIcon from '@mui/icons-material/Close'
import { useAppSelector } from '@/store'
import { useAuth } from '@/providers/AuthProvider'
import { useLocationRef } from '@/contexts/RefContext'
import { oocApi } from '@/lib/apiClient'
import { oocUrls } from '@/lib/urlBuilder'
import { logWarning } from '@/services/errorService'
import { formatPhoneNumber } from '@/lib/validation'
import { colors, breakpoints, typography, spacing, radius } from '@/styles/designTokens'
import { DrawerPuller } from '@/components/common/ui/DrawerPuller.styled'
import {
	StyledPopoverContent,
	StyledHeader,
	StyledPhoneNumber,
	StyledInfoSection,
	StyledMenuItem,
	StyledMenuIcon,
	StyledModalBackdrop,
	CloseButton,
} from './AccountPopover.styled'
import type { AccountPopoverProps } from './AccountPopover.types'

export function AccountPopover({
	isOpen,
	anchorEl,
	onClose,
	onMouseEnter,
	onMouseLeave,
}: AccountPopoverProps) {
	const { t } = useFormatMessage()
	const { logout } = useAuth()
	const navigate = useNavigate()
	const ref = useLocationRef()
	const authUser = useAppSelector((state) => state.auth.user)
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	const handleEditProfile = useCallback(() => {
		void navigate(`/${ref}/user-profile`)
		onClose()
	}, [navigate, ref, onClose])

	const handleLogout = useCallback(() => {
		void (async () => {
			try {
				await oocApi.del(oocUrls.logout())
			} catch (error) {
				logWarning('[AccountPopover] Logout API failed', { error: String(error) })
			} finally {
				await logout()
				onClose()
			}
		})()
	}, [logout, onClose])

	if (!authUser) return null

	const content = (
		<StyledPopoverContent>
			{/* Drag pill — shown only on mobile via DrawerPuller's built-in responsive styles */}
			<DrawerPuller aria-hidden="true" />
			<StyledHeader>
				<StyledPhoneNumber>
					{authUser.firstName && authUser.lastName
						? `${authUser.firstName} ${authUser.lastName}`
						: formatPhoneNumber(authUser.phone || '') || authUser.email}
				</StyledPhoneNumber>
				{isMobile && (
					<CloseButton onClick={onClose} aria-label={t('signin:modal:closeButton')}>
						<CloseIcon sx={{ fontSize: typography.fontSizeIconLg }} aria-hidden />
					</CloseButton>
				)}
			</StyledHeader>
			<StyledInfoSection>
				<StyledMenuItem type="button" onClick={handleEditProfile}>
					<StyledMenuIcon>
						<EditOutlinedIcon
							sx={{ fontSize: typography.fontSizeIconLg }}
							aria-hidden
						/>
					</StyledMenuIcon>
					{t('signin:accountPopover:editProfile')}
				</StyledMenuItem>
				<StyledMenuItem
					type="button"
					onClick={(e) => {
						e.preventDefault()
						void handleLogout()
					}}
				>
					<StyledMenuIcon>
						<LogoutIcon sx={{ fontSize: typography.fontSizeIconLg }} aria-hidden />
					</StyledMenuIcon>
					{t('signin:accountPopover:logoutButton')}
				</StyledMenuItem>
			</StyledInfoSection>
		</StyledPopoverContent>
	)

	if (isMobile) {
		return (
			<Modal
				open={isOpen}
				onClose={onClose}
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					[`@media (max-width: ${breakpoints.compact}px)`]: {
						alignItems: 'flex-end',
						justifyContent: 'center',
					},
				}}
			>
				<StyledModalBackdrop>{content}</StyledModalBackdrop>
			</Modal>
		)
	}

	return (
		<Popover
			open={isOpen}
			anchorEl={anchorEl}
			onClose={onClose}
			anchorOrigin={{
				vertical: 'bottom',
				horizontal: 'right',
			}}
			transformOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			disableScrollLock
			// The invisible backdrop must not intercept the pointer, otherwise
			// moving from the popover back to the avatar (across the gap) fires a
			// spurious mouseleave and the popover flickers closed. Disabling
			// pointer events on the root lets the mouse pass through to the avatar;
			// the paper re-enables them so the content stays hoverable/clickable.
			sx={{ pointerEvents: 'none' }}
			slotProps={{
				paper: {
					// Match the inner content radius and clip children so the
					// Paper's own corners don't peek out behind the rounded content.
					// The border lives here (not on the inner content) because the
					// Paper is the outermost visible element — a border on the clipped
					// inner content would be hidden by the Paper's overflow: hidden.
					sx: {
						marginTop: `${spacing.sm}px`,
						pointerEvents: 'auto',
						borderRadius: `${radius.sheet}px`,
						overflow: 'hidden',
						border: `1px solid ${colors.border}`,
					},
					onMouseEnter,
					onMouseLeave,
				},
			}}
		>
			{content}
		</Popover>
	)
}
